import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LLM_ROUTINGS, type LlmModelInput, type LlmRouting, type SmartRouting } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, cx, Dialog, ErrorText, Field, Input, PageHeader, Select, Spinner, Switch } from "../components/ui.tsx";

type LlmData = Awaited<ReturnType<typeof load>>;
type Provider = LlmData["providers"][number];
const load = () => unwrap(api.admin.llm.$get());

const ROUTING_LABEL: Record<LlmRouting, string> = { throughput: "Fastest (highest throughput)", latency: "Quickest to start (lowest latency)", price: "Cheapest (OpenRouter default)" };

const PRESETS = [
  { id: "openai", name: "OpenAI", baseUrl: "https://api.openai.com/v1" },
  { id: "anthropic", name: "Anthropic", baseUrl: "https://api.anthropic.com/v1" },
  { id: "gemini", name: "Google Gemini", baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai" },
  { id: "custom", name: "Other (OpenAI-compatible)", baseUrl: "" },
] as const;

const price = (n: number) => (n === 0 ? "—" : `$${n < 1 ? n.toFixed(3).replace(/0+$/, "") : n.toFixed(2)}`);
const ctx = (n: number | null) => (n ? (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : `${Math.round(n / 1000)}K`) : "—");

export function ModelsPage() {
  const q = useQuery({ queryKey: ["llm"], queryFn: load });
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  return (
    <>
      <PageHeader title="Models & providers" description="Which AI models assistants may use. The first model is the default; the rest are tried in order when one fails." />
      <ModelList data={q.data} />
      <SmartRoutingCard data={q.data} />
      <BackgroundModelCard />
      <Providers data={q.data} />
    </>
  );
}

/** The model that works in the background for the company: forming memories, deciding what is shared, checking skills. */
function BackgroundModelCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["memory"], queryFn: () => unwrap(api.admin.memory.$get()) });
  const [model, setModel] = useState<string | null | undefined>(undefined);
  const saved = q.data?.settings.memoryModel ?? null;
  const value = model === undefined ? saved : model;
  const save = useMutation({
    mutationFn: () => unwrap(api.admin.memory.$put({ json: { memoryModel: value } })),
    onSuccess: () => {
      setModel(undefined);
      void qc.invalidateQueries({ queryKey: ["memory"] });
    },
  });
  return (
    <Card className="mb-8 space-y-4 p-5">
      <div>
        <h2 className="font-semibold">Background models</h2>
        <p className="text-xs text-[var(--muted)]">Work done for the company rather than for one person. Billed to the company, not to anyone's budget.</p>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorText error={q.error} />
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1 sm:max-w-md">
            <Field
              label="Memory and review model"
              hint={`Forms memories from conversations, decides what is shared under the sharing rules, and checks skills for sensitive data. Automatic: the memory engine's own model if it is allowed here, else ${q.data.models[0]?.label ?? "the default model"}.`}
            >
              <Select value={value ?? ""} onChange={(e) => setModel(e.target.value || null)}>
                <option value="">Automatic</option>
                {q.data.models.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </Select>
            </Field>
          </div>
          <Button variant="primary" disabled={save.isPending || value === saved} onClick={() => save.mutate()}>Save</Button>
          {save.isSuccess && value === saved && <span className="pb-2 text-sm text-[var(--muted)]">Saved</span>}
          <div className="w-full"><ErrorText error={save.error} /></div>
        </div>
      )}
    </Card>
  );
}

/** Everyday chat on the default model; bigger work on a stronger task model. */
function SmartRoutingCard({ data }: { data: LlmData }) {
  const qc = useQueryClient();
  const [r, setR] = useState<SmartRouting>(data.routing);
  useEffect(() => setR(data.routing), [data.routing]);
  const dirty = JSON.stringify(r) !== JSON.stringify(data.routing);
  const head = data.models[0];
  const candidates = data.models.slice(1);
  const save = useMutation({
    mutationFn: () => unwrap(api.admin.llm.routing.$put({ json: r })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["llm"] });
      void qc.invalidateQueries({ queryKey: ["runtimes"] });
    },
  });
  const set = (patch: Partial<SmartRouting>) => setR((x) => ({ ...x, ...patch }));
  return (
    <Card className="mb-8 space-y-4 p-5">
      <div>
        <h2 className="font-semibold">Smart routing</h2>
        <p className="text-xs text-[var(--muted)]">
          Everyday chat runs on the default model{head ? <> (<b>{head.label || head.id}</b>)</> : null}; bigger work moves to a stronger task model. The other models stay the fallbacks for both.
        </p>
      </div>
      <Switch label="Route bigger work to a task model" checked={r.enabled} onChange={(v) => set({ enabled: v, taskModel: r.taskModel ?? candidates[0]?.id ?? null })} />
      {r.enabled && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Task model" hint="Used for messages judged as bigger work, long-running turns, subagents and scheduled tasks. Changing it restarts every assistant.">
            <Select value={r.taskModel ?? ""} onChange={(e) => set({ taskModel: e.target.value || null })}>
              <option value="">Choose a model…</option>
              {candidates.map((m) => <option key={m.id} value={m.id}>{m.label || m.id}</option>)}
            </Select>
          </Field>
          <Field label="Classifier model (optional)" hint="A small, fast model from the default model's provider that reads each new message and answers CHAT or TASK. Leave empty to rely on the step count and the keyword.">
            <Input value={r.classifierModel ?? ""} onChange={(e) => set({ classifierModel: e.target.value.trim() || null })} placeholder="e.g. gemini-3.1-flash-lite" />
          </Field>
          <Field label="Move up after this many tool steps" hint="A turn on the default model that keeps going this long is clearly bigger work.">
            <Input type="number" min={1} max={50} value={r.escalateAfterSteps} onChange={(e) => set({ escalateAfterSteps: Math.max(1, Math.min(50, Number(e.target.value) || 1)) })} />
          </Field>
          <Field label="Keyword" hint="People can start a message with this to use the task model straight away.">
            <Input value={r.keyword} onChange={(e) => set({ keyword: e.target.value })} />
          </Field>
        </div>
      )}
      <ErrorText error={save.error} />
      {save.data?.applied && <p className="text-xs text-[var(--muted)]">Saved. {save.data.applied.assistantsRestarting} assistants are restarting to pick it up.</p>}
      <div className="flex justify-end">
        <Button variant="primary" disabled={!dirty || save.isPending || (r.enabled && !r.taskModel)} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save routing"}
        </Button>
      </div>
    </Card>
  );
}

function ModelList({ data }: { data: LlmData }) {
  const qc = useQueryClient();
  const initial = useMemo<LlmModelInput[]>(
    () => data.models.map((m) => ({ id: m.id, providerId: m.providerId, label: m.label, inputPerM: m.inputPerM, outputPerM: m.outputPerM, contextLength: m.contextLength })),
    [data.models],
  );
  const [list, setList] = useState(initial);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    setList(initial);
  }, [initial]);
  const dirty = JSON.stringify(list) !== JSON.stringify(initial);
  const providerName = (id: string) => data.providers.find((p) => p.id === id)?.name ?? id;

  const save = useMutation({
    mutationFn: () => unwrap(api.admin.llm.models.$put({ json: { models: list } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["llm"] });
      void qc.invalidateQueries({ queryKey: ["runtimes"] });
    },
  });
  const move = (i: number, d: -1 | 1) =>
    setList((l) => {
      const n = [...l];
      [n[i], n[i + d]] = [n[i + d]!, n[i]!];
      return n;
    });

  return (
    <Card className="mb-8">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--line)] px-5 py-4">
        <div>
          <h2 className="font-semibold">Allowed models</h2>
          <p className="text-xs text-[var(--muted)]">Assistants asking for any other model get the default. Prices are USD per million tokens.</p>
        </div>
        <Button onClick={() => setAdding(true)}>Add model</Button>
      </div>
      <div className="divide-y divide-[var(--line)]">
        {list.map((m, i) => (
          <div key={m.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
            <div className="w-24 shrink-0">{i === 0 ? <Badge tone="accent">Default</Badge> : <Badge>Fallback {i}</Badge>}</div>
            <div className="min-w-0 flex-1 basis-48">
              <div className="truncate font-mono text-sm">{m.id}</div>
              <div className="truncate text-xs text-[var(--muted)]">
                {m.label && `${m.label} · `}
                {providerName(m.providerId)}
              </div>
            </div>
            <div className="w-40 text-xs text-[var(--muted)]">
              In {price(m.inputPerM)} · Out {price(m.outputPerM)}
              <br />
              Context {ctx(m.contextLength)}
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" className="h-8 w-8 px-0" disabled={i === 0} onClick={() => move(i, -1)} title="Move up">↑</Button>
              <Button variant="ghost" className="h-8 w-8 px-0" disabled={i === list.length - 1} onClick={() => move(i, 1)} title="Move down">↓</Button>
              <Button variant="ghost" className="h-8" disabled={list.length === 1} onClick={() => setList((l) => l.filter((x) => x.id !== m.id))}>Remove</Button>
            </div>
          </div>
        ))}
      </div>
      <div className={cx("flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] px-5 py-3", dirty && "bg-amber-50")}>
        <span className="text-sm text-[var(--muted)]">
          {save.isSuccess && !dirty
            ? `Saved. ${save.data.applied.assistantsRestarting} assistant(s) restarting with the new models.`
            : dirty
              ? "Unsaved changes. Saving restarts every assistant."
              : `Revision ${data.revision}`}
        </span>
        <div className="flex gap-2">
          {dirty && <Button variant="ghost" onClick={() => setList(initial)}>Discard</Button>}
          <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => confirm("Save models and restart every assistant?") && save.mutate()}>
            {save.isPending ? <Spinner /> : null}Save & restart assistants
          </Button>
        </div>
      </div>
      <div className="px-5 pb-3"><ErrorText error={save.error} /></div>
      {adding && (
        <AddModelDialog
          providers={data.providers}
          existing={list.map((m) => m.id)}
          onClose={() => setAdding(false)}
          onAdd={(m) => {
            setList((l) => [...l, m]);
            setAdding(false);
          }}
        />
      )}
    </Card>
  );
}

function AddModelDialog({ providers, existing, onClose, onAdd }: { providers: Provider[]; existing: string[]; onClose: () => void; onAdd: (m: LlmModelInput) => void }) {
  const usable = providers.filter((p) => p.enabled && p.keySource !== "missing");
  const [providerId, setProviderId] = useState(usable[0]?.id ?? "");
  const [search, setSearch] = useState("");
  const [manual, setManual] = useState({ id: "", inputPerM: 0, outputPerM: 0 });
  const catalog = useQuery({
    queryKey: ["llm-catalog", providerId],
    queryFn: () => unwrap(api.admin.llm.providers[":id"].models.$get({ param: { id: providerId } })),
    enabled: !!providerId,
    staleTime: 5 * 60_000,
  });
  const matches = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (catalog.data?.models ?? []).filter((m) => !existing.includes(m.id) && (!s || m.id.toLowerCase().includes(s) || m.name.toLowerCase().includes(s))).slice(0, 60);
  }, [catalog.data, search, existing]);

  return (
    <Dialog open onClose={onClose} title="Add model" wide>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
          <Field label="Provider">
            <Select value={providerId} onChange={(e) => setProviderId(e.target.value)}>
              {usable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Search">
            <Input autoFocus placeholder="e.g. claude, gpt, glm, gemini" value={search} onChange={(e) => setSearch(e.target.value)} />
          </Field>
        </div>
        <ErrorText error={catalog.error} />
        <div className="max-h-80 overflow-y-auto rounded-lg border border-[var(--line)]">
          {catalog.isPending ? (
            <div className="grid place-items-center py-10"><Spinner /></div>
          ) : matches.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-[var(--muted)]">No matches.</p>
          ) : (
            matches.map((m) => (
              <button
                key={m.id}
                onClick={() => onAdd({ id: m.id, providerId, label: m.name === m.id ? "" : m.name, inputPerM: m.inputPerM ?? 0, outputPerM: m.outputPerM ?? 0, contextLength: m.contextLength })}
                className="flex w-full items-center gap-4 border-b border-[var(--line)] px-4 py-2.5 text-left last:border-0 hover:bg-[var(--panel-2)]"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-sm">{m.id}</div>
                  {m.name !== m.id && <div className="truncate text-xs text-[var(--muted)]">{m.name}</div>}
                </div>
                <div className="shrink-0 text-right text-xs text-[var(--muted)]">
                  {m.inputPerM !== null ? `${price(m.inputPerM)} / ${price(m.outputPerM ?? 0)}` : "price n/a"}
                  <br />
                  {ctx(m.contextLength)}
                </div>
              </button>
            ))
          )}
        </div>
        <details className="rounded-lg border border-[var(--line)] px-4 py-3">
          <summary className="text-sm font-medium">Model not listed? Add it by ID</summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_7rem_7rem_auto] sm:items-end">
            <Field label="Model ID"><Input value={manual.id} onChange={(e) => setManual({ ...manual, id: e.target.value })} /></Field>
            <Field label="In $/M"><Input type="number" min={0} step="0.001" value={manual.inputPerM} onChange={(e) => setManual({ ...manual, inputPerM: Number(e.target.value) })} /></Field>
            <Field label="Out $/M"><Input type="number" min={0} step="0.001" value={manual.outputPerM} onChange={(e) => setManual({ ...manual, outputPerM: Number(e.target.value) })} /></Field>
            <Button disabled={!manual.id.trim() || existing.includes(manual.id.trim())} onClick={() => onAdd({ id: manual.id.trim(), providerId, label: "", inputPerM: manual.inputPerM, outputPerM: manual.outputPerM, contextLength: null })}>Add</Button>
          </div>
        </details>
      </div>
    </Dialog>
  );
}

function Providers({ data }: { data: LlmData }) {
  const [editing, setEditing] = useState<Provider | "new" | null>(null);
  const used = (id: string) => data.models.filter((m) => m.providerId === id).length;
  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">Providers</h2>
        <Button onClick={() => setEditing("new")}>Add provider</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {data.providers.map((p) => (
          <Card key={p.id} className="flex items-start justify-between gap-3 p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{p.name}</span>
                {!p.enabled && <Badge>Disabled</Badge>}
                {p.keySource === "missing" && <Badge tone="bad">No key</Badge>}
              </div>
              <div className="truncate text-xs text-[var(--muted)]">{p.baseUrl}</div>
              <div className="mt-1 text-xs text-[var(--muted)]">
                {p.keySource === "deployment" ? "Key from the deployment secret" : p.keySource === "stored" ? "Key stored encrypted" : "Add an API key to use it"} · {used(p.id)} model(s)
                {p.kind === "openrouter" && <> · routing: {ROUTING_LABEL[p.routing]}</>}
              </div>
            </div>
            <Button variant="ghost" className="h-8" onClick={() => setEditing(p)}>Edit</Button>
          </Card>
        ))}
      </div>
      {editing && <ProviderDialog provider={editing === "new" ? null : editing} inUse={editing !== "new" && used(editing.id) > 0} onClose={() => setEditing(null)} />}
    </>
  );
}

function ProviderDialog({ provider, inUse, onClose }: { provider: Provider | null; inUse: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<string>(PRESETS[0].id);
  const [id, setId] = useState(provider?.id ?? PRESETS[0].id);
  const [name, setName] = useState(provider?.name ?? PRESETS[0].name);
  const [baseUrl, setBaseUrl] = useState(provider?.baseUrl ?? PRESETS[0].baseUrl);
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(provider?.enabled ?? true);
  const [routing, setRouting] = useState<LlmRouting>(provider?.routing ?? "throughput");
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["llm"] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.admin.llm.providers[":id"].$put({
          param: { id },
          json: { name, kind: provider?.kind ?? "openai", baseUrl, apiKey: apiKey || undefined, enabled, routing },
        }),
      ),
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => unwrap(api.admin.llm.providers[":id"].$delete({ param: { id } })), onSuccess: done });

  return (
    <Dialog open onClose={onClose} title={provider ? `Edit ${provider.name}` : "Add provider"}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {!provider && (
          <Field label="Type">
            <Select
              value={preset}
              onChange={(e) => {
                const p = PRESETS.find((x) => x.id === e.target.value)!;
                setPreset(p.id);
                setId(p.id === "custom" ? "" : p.id);
                setName(p.id === "custom" ? "" : p.name);
                setBaseUrl(p.baseUrl);
              }}
            >
              {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input required value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="ID" hint="Lowercase, e.g. my-vllm"><Input required disabled={!!provider} value={id} onChange={(e) => setId(e.target.value.toLowerCase())} /></Field>
        </div>
        <Field label="Base URL" hint="OpenAI-compatible endpoint (…/v1)."><Input required type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} /></Field>
        <Field
          label="API key"
          hint={provider?.keySource === "deployment" ? "Leave blank to keep using the deployment secret's key." : provider?.keySource === "stored" ? "Stored encrypted. Leave blank to keep it." : undefined}
        >
          <Input type="password" autoComplete="off" required={!provider || provider.keySource === "missing"} value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        </Field>
        {provider?.kind === "openrouter" && (
          <Field label="Routing" hint="OpenRouter serves each model from many hosts. Fastest was ~3–4× quicker than cheapest in our tests, for about 2× the price of uncached input. Changing this restarts nobody.">
            <Select value={routing} onChange={(e) => setRouting(e.target.value as LlmRouting)}>
              {LLM_ROUTINGS.map((r) => <option key={r} value={r}>{ROUTING_LABEL[r]}</option>)}
            </Select>
          </Field>
        )}
        <Switch label="Enabled" hint={inUse ? "Its models become unavailable while disabled." : undefined} checked={enabled} onChange={setEnabled} />
        {inUse && <p className="text-xs text-[var(--muted)]">This provider serves allowed models, so saving restarts every assistant.</p>}
        <ErrorText error={save.error ?? remove.error} />
        <div className="flex items-center justify-between gap-2 border-t border-[var(--line)] pt-4">
          {provider && !inUse ? <Button type="button" variant="danger" onClick={() => confirm(`Remove ${provider.name}?`) && remove.mutate()}>Remove</Button> : <span />}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={save.isPending}>Save</Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
